import React, { createContext, useMemo, useState, useCallback, useEffect } from "react";

import { useCurrentClient } from "hooks/api/use-clients";
import { fetchTheme, fetchTypekitFonts, fetchGoogleFonts } from "hooks/api/use-theme";
import { getEmailTypesRequest } from "hooks/api/use-email-types";

import { transformThemeDataOnGet } from "containers/admin/clients/client/theme-page/preview/helpers/formatters-on-get-data/formatters-on-get-data";
import { formatDataToBeeConfigs } from "containers/admin/clients/client/theme-page/preview/helpers/bee-data-formatter/bee-data-formatter";

import {
  ColorsFormData,
  ThemeContextData,
  AppThemeData,
  AppFontsFormData,
  AppTypographyFormData,
  AppButtonFormData,
  APIThemeData,
  AppBannerFormData,
  AppLayoutFormData,
  ViewModeOption,
} from "containers/admin/clients/client/theme-page/preview/preview.types";
import { GoogleFont } from "containers/admin/clients/client/theme-page/preview/sub-tabs/fonts/google-fonts/google-fonts.types";
import { TypekitFont } from "types";
import { BeeConfigExtended, BrandStyleData } from "types/bee";
import { ISenderDetails } from "containers/admin/clients/client/components/email-types/components/email-types-senders-form/email-types-senders-form.types";
import { IEmailType } from "containers/admin/clients/client/components/email-types/email-types.types";

import { DEFAULT_SENDER_DATA } from "containers/admin/clients/client/components/email-types/email-types.constants";
import { ADOBE_FONTS } from "containers/admin/clients/client/theme-page/preview/preview.constants";
import THEME_DEFAULT_DATA from "containers/admin/clients/client/theme-page/theme-default-data.json";

const INITIAL_THEME_STATE: ThemeContextData = {
  colors: THEME_DEFAULT_DATA.colors,
  setColors: () => {},
  fonts: THEME_DEFAULT_DATA.fonts,
  setFonts: () => {},
  typography: THEME_DEFAULT_DATA.typography,
  setTypography: () => {},
  button: THEME_DEFAULT_DATA.button,
  setButton: () => {},
  banner: THEME_DEFAULT_DATA.banner,
  setBanner: () => {},
  layout: THEME_DEFAULT_DATA.layout,
  setLayout: () => {},
  beeConfigData: null,
  brandStyleData: null,
  isEditMode: false,
  setIsEditMode: () => {},
  viewMode: "Mobile",
  setViewMode: () => {},
  isSettingsOpen: true,
  setIsSettingsOpen: () => {},
  isFallbackFontApplied: false,
  setIsFallbackFontApplied: () => {},
  themeShouldBeUpdated: false,
  setThemeShouldBeUpdated: () => {},
  getUpdatedThemeContext: async () => THEME_DEFAULT_DATA,
  googleFontsList: [],
  googleFontsFetchError: "",
  adobeFontsList: [],
  setAdobeFontsList: () => {},
  adobeFontsFetchError: "",
  themeError: "",
  senderDetails: undefined,
};

export const ThemePreviewContext = createContext<ThemeContextData>(INITIAL_THEME_STATE);

export const ThemePreviewProvider = (props: React.PropsWithChildren) => {
  const [colors, setColors] = useState<ColorsFormData | null>(null);
  const [fonts, setFonts] = useState<AppFontsFormData | null>(null);
  const [typography, setTypography] = useState<AppTypographyFormData | null>(null);
  const [button, setButton] = useState<AppButtonFormData | null>(null);
  const [banner, setBanner] = useState<AppBannerFormData | null>(null);
  const [layout, setLayout] = useState<AppLayoutFormData | null>(null);
  const [beeConfigData, setBeeConfigData] = useState<BeeConfigExtended | null>(null);
  const [brandStyleData, setBrandStyleData] = useState<BrandStyleData | null>(null);

  const [themeError, setThemeError] = useState<string>(INITIAL_THEME_STATE.themeError);
  const [isEditMode, setIsEditMode] = useState<boolean>(INITIAL_THEME_STATE.isEditMode);
  const [viewMode, setViewMode] = useState<ViewModeOption>(INITIAL_THEME_STATE.viewMode);

  const [isFallbackFontApplied, setIsFallbackFontApplied] = useState<boolean>(
    INITIAL_THEME_STATE.isFallbackFontApplied,
  );
  const [themeShouldBeUpdated, setThemeShouldBeUpdated] = useState<boolean>(
    INITIAL_THEME_STATE.themeShouldBeUpdated,
  );
  const [isSettingsOpen, setIsSettingsOpen] = useState<boolean>(INITIAL_THEME_STATE.isSettingsOpen);
  //Google Fonts
  const [googleFontsList, setGoogleFontsList] = useState<GoogleFont[]>(
    INITIAL_THEME_STATE.googleFontsList,
  );
  const [googleFontsFetchError, setGoogleFontsFetchError] = useState<string>(
    INITIAL_THEME_STATE.adobeFontsFetchError,
  );
  //Adobe Fonts
  const [adobeFontsList, setAdobeFontsList] = useState<TypekitFont[]>(
    INITIAL_THEME_STATE.adobeFontsList,
  );
  const [adobeFontsFetchError, setAdobeFontsFetchError] = useState<string>(
    INITIAL_THEME_STATE.adobeFontsFetchError,
  );
  const [senderDetails, setSenderDetails] = useState<ISenderDetails | undefined>(
    INITIAL_THEME_STATE.senderDetails,
  );

  const client = useCurrentClient();

  useEffect(() => {
    const loadGoogleFonts = async () => {
      try {
        const data = await fetchGoogleFonts();

        setGoogleFontsList(data.items);
      } catch (err) {
        setGoogleFontsFetchError(
          err instanceof Error ? err.message : "Failed to load Google fonts",
        );
      }
    };

    loadGoogleFonts();
  }, []);

  // Fetch Adobe fonts when project ID changes or when theme loads
  useEffect(() => {
    const fetchAdobeFonts = async () => {
      const adobeFontId = fonts?.[ADOBE_FONTS.key];

      if (!adobeFontId) {
        setAdobeFontsList([]);
        setAdobeFontsFetchError("");
        return;
      }

      try {
        const response = await fetchTypekitFonts(client.id, adobeFontId);
        setAdobeFontsList(response?.kit?.families);
        setAdobeFontsFetchError("");
      } catch (err) {
        console.error("Error fetching Adobe Fonts:", err);
        setAdobeFontsFetchError("Invalid Project ID");
        setAdobeFontsList([]);
      }
    };

    fetchAdobeFonts();
    // eslint-disable-next-line
  }, [fonts?.adobeFontsProjectId, client.id]);

  // Fetch sender details for preview
  useEffect(() => {
    const fetchSenderDetails = async () => {
      try {
        const emailTypes = (await getEmailTypesRequest(client.id)) as unknown as IEmailType[];

        // Get the default email type and its default sender
        const defaultEmailType = emailTypes.find((emailType) => emailType.defaultForClient);
        if (defaultEmailType?.senders?.length) {
          const defaultSender = defaultEmailType.senders.find(
            (sender) => sender.defaultForEmailType,
          );
          if (defaultSender) {
            setSenderDetails(defaultSender);
          }
        }
      } catch (e) {
        console.error("Error while fetching email types for preview", e);
        // Fall back to default sender data if there's an error
        setSenderDetails(DEFAULT_SENDER_DATA);
      }
    };

    if (client.id) {
      fetchSenderDetails();
    }
  }, [client.id]);

  const fetchAndApplyTheme = async (clientId: string): Promise<AppThemeData | null> => {
    try {
      const results: APIThemeData = await fetchTheme(clientId);

      const configs = formatDataToBeeConfigs(results);

      const hasThemeData =
        results.colors && results.fonts && results.typography && results.button && results.layout;

      const theme: AppThemeData = hasThemeData
        ? transformThemeDataOnGet(results)
        : {
            colors: INITIAL_THEME_STATE.colors as ColorsFormData,
            fonts: INITIAL_THEME_STATE.fonts as AppFontsFormData,
            typography: INITIAL_THEME_STATE.typography as AppTypographyFormData,
            button: INITIAL_THEME_STATE.button as AppButtonFormData,
            banner: INITIAL_THEME_STATE.banner as AppBannerFormData,
            layout: INITIAL_THEME_STATE.layout as AppLayoutFormData,
          };

      setColors(theme.colors);
      setFonts(theme.fonts);
      setTypography(theme.typography);
      setButton(theme.button);
      setBanner(theme.banner);
      setLayout(theme.layout);

      if (configs) {
        setBeeConfigData(configs.beeConfig as BeeConfigExtended);
        setBrandStyleData(configs.brandStyleConfig as BrandStyleData);
      }
      return theme;
    } catch (error) {
      console.error("Error fetching theme config", error);

      setThemeError(error?.response?.data?.title);

      return null;
    }
  };

  // reset theme context to last saved values
  const getUpdatedThemeContext = useCallback(async (): Promise<AppThemeData> => {
    const theme = await fetchAndApplyTheme(client.id);

    if (theme) return theme;

    return {
      colors: THEME_DEFAULT_DATA.colors,
      fonts: THEME_DEFAULT_DATA.fonts,
      typography: THEME_DEFAULT_DATA.typography,
      button: THEME_DEFAULT_DATA.button,
      banner: THEME_DEFAULT_DATA.banner,
      layout: THEME_DEFAULT_DATA.layout,
    };
    // eslint-disable-next-line
  }, []);

  const getThemeForm = useCallback(async () => {
    await fetchAndApplyTheme(client.id);
  }, [client.id]);

  useEffect(() => {
    getThemeForm();
    // eslint-disable-next-line
  }, []);

  const value = useMemo(
    () => ({
      colors,
      setColors,
      fonts,
      setFonts,
      typography,
      setTypography,
      button,
      beeConfigData,
      brandStyleData,
      setButton,
      banner,
      setBanner,
      layout,
      setLayout,
      isEditMode,
      setIsEditMode,
      viewMode,
      setViewMode,
      isSettingsOpen,
      setIsSettingsOpen,
      isFallbackFontApplied,
      setIsFallbackFontApplied,
      setThemeShouldBeUpdated,
      themeShouldBeUpdated,
      getUpdatedThemeContext,
      themeError,
      googleFontsFetchError,
      googleFontsList,
      adobeFontsList,
      setAdobeFontsList,
      adobeFontsFetchError,
      senderDetails,
    }),
    [
      //theme data we change all the time
      colors,
      setColors,
      fonts,
      setFonts,
      typography,
      setTypography,
      button,
      setButton,
      banner,
      setBanner,
      layout,
      setLayout,
      beeConfigData,
      brandStyleData,
      //theme modes
      isEditMode,
      setIsEditMode,
      viewMode,
      setViewMode,
      isFallbackFontApplied,
      setIsFallbackFontApplied,
      isSettingsOpen,
      setIsSettingsOpen,
      //updating data
      setThemeShouldBeUpdated,
      themeShouldBeUpdated,
      getUpdatedThemeContext,
      //errors
      themeError,
      googleFontsFetchError,
      adobeFontsFetchError,
      //constant data we fetch only once
      googleFontsList,
      adobeFontsList,
      setAdobeFontsList,
      senderDetails,
    ],
  );

  return <ThemePreviewContext.Provider value={value} {...props} />;
};

export const useThemePreviewContext = () => {
  const context = React.useContext(ThemePreviewContext);
  if (context === undefined) {
    throw new Error("useThemePreviewContext must be used within a ThemePreviewProvider");
  }
  return context;
};
